import { JSONSchemaType } from "ajv";

import {
  AssociationRecord,
  AssociationType,
} from "@/shared/domain/association";
import {
  CanonicalDataType,
  MessagePathAttr,
  MessagePathPart,
  MessagePathPartType,
  MessagePathSlice,
  RepresentationStorageFormat,
} from "@/shared/domain/topics";

export interface TopicData {
  topic: {
    id: string; // TopicRecord::topic_id
    name: string; // TopicRecord::topic_name
    association: AssociationRecord; // TopicRecord::association
    startTime?: string; // TopicRecord::start_time
    endTime?: string; // TopicRecord::end_time
    messageCount?: number; // TopicRecord::message_count
  };
  messagePath: {
    id: string; // MessagePathRecord::topic_message_path_id
    dotPath: string; // MessagePathRecord::message_path, e.g., "transforms.transform.translation.x"
    metadata: Record<string, unknown>; // MessagePathRecord::metadata
    // Ordered list of parts that make up the message path.
    // E.g., for a message path like "transforms.transform.translation.x"
    // this would be:
    // [
    //   { type: "Attr", attribute: "transforms", dataType: <CanonicalDataType.Array> },
    //   { type: "Slice", start: 0, end: 10, step: 1 },
    //   { type: "Attr", attribute: "transform", dataType: <CanonicalDataType.Object> },
    //   { type: "Attr", attribute: "translation", dataType: <CanonicalDataType.Object> },
    //   { type: "Attr", attribute: "x", dataType: <CanonicalDataType.Number> },
    // ]
    parts: MessagePathPart[];
  };
  representation: {
    association: AssociationRecord;
    id: string;
    format: RepresentationStorageFormat;
  };
}

/**
 * Binds a topic/message path to a specific file.
 *
 * This is a user-friendly way of collecting inputs to find TopicData
 * for a specific message path in a specific file.
 *
 * It's used for helping the user pick data sources in various components.
 */
export interface TopicDataPathBinding {
  fileId: string;
  path: string;
}

const messagePathAttrSchema: JSONSchemaType<MessagePathAttr> = {
  type: "object",
  properties: {
    type: {
      type: "string",
      enum: [MessagePathPartType.Attr],
    },
    attribute: { type: "string" },
    dataType: {
      type: "string",
      enum: Object.values(CanonicalDataType),
    },
  },
  required: ["type", "attribute", "dataType"],
  additionalProperties: false,
};

const messagePathSliceSchema: JSONSchemaType<MessagePathSlice> = {
  type: "object",
  properties: {
    type: {
      type: "string",
      enum: [MessagePathPartType.Slice],
    },
    start: { type: "number" },
    end: { type: "number", nullable: true },
  },
  required: ["type", "start"],
  additionalProperties: false,
};

const messagePathPartSchema: JSONSchemaType<MessagePathPart> = {
  anyOf: [messagePathAttrSchema, messagePathSliceSchema],
};

const associationSchema: JSONSchemaType<AssociationRecord> = {
  type: "object",
  properties: {
    association_id: { type: "string" },
    association_type: {
      type: "string",
      enum: [AssociationType.Dataset, AssociationType.File],
    },
    association_version: { type: "number", nullable: true },
  },
  required: ["association_id", "association_type"],
  additionalProperties: true,
};

const representationSchema: JSONSchemaType<TopicData["representation"]> = {
  type: "object",
  properties: {
    association: associationSchema,
    id: { type: "string" },
    format: {
      type: "string",
      enum: [
        RepresentationStorageFormat.MCAP,
        RepresentationStorageFormat.PARQUET,
      ],
    },
  },
  required: ["association", "id", "format"],
  additionalProperties: true,
};

export const topicDataSchema: JSONSchemaType<TopicData> = {
  type: "object",
  properties: {
    topic: {
      type: "object",
      properties: {
        id: { type: "string" },
        name: { type: "string" },
        association: associationSchema,
        startTime: { type: "string", nullable: true },
        endTime: { type: "string", nullable: true },
        messageCount: { type: "number", nullable: true },
      },
      required: ["name", "association"],
      additionalProperties: true,
    },
    messagePath: {
      type: "object",
      properties: {
        id: { type: "string" },
        dotPath: { type: "string" },
        metadata: {
          type: "object",
          additionalProperties: true,
        },
        parts: {
          type: "array",
          items: messagePathPartSchema,
        },
      },
      required: ["dotPath", "metadata", "parts"],
      additionalProperties: true,
    },
    representation: representationSchema,
  },
  required: ["topic", "messagePath", "representation"],
  additionalProperties: true,
};
